import { TZDate } from '@date-fns/tz';
import { Box, TableCell, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormatDate } from 'src/components/atoms/format-date';
import { Status } from 'src/components/atoms/status';
import { BookingStatus } from 'src/features/bookings/api/types/response/booking.response';
import { bookingPresenceColors } from 'src/features/bookings/constants/booking-presence/booking-presence-colors';
import { bookingPresenceTranslations } from 'src/features/bookings/constants/booking-presence/booking-presence-translations';
import { bookingStateColors } from 'src/features/bookings/constants/booking-state/booking-state-colors';
import { bookingStateTranslations } from 'src/features/bookings/constants/booking-state/booking-state-translations';
import { getBookingStatus } from 'src/features/bookings/utils/get-booking-status';
import { useOrganizationDetails } from 'src/features/organization-details/hooks/api/use-organization-details';
import { spaceTypesTranslations } from 'src/features/spaces/constants/space-types-translations';
import { SpaceType } from 'src/features/spaces/types/space-type';
import { dateFormats } from 'src/utils/date-formats';

interface Props {
  employeeEmail: string;
  spaceName: string;
  spaceType: SpaceType;
  spaceFloor: number;
  from: number;
  to: number;
  confirmedAt: string | null;
  status: BookingStatus;
}

export const BookingsTableRow = ({
  employeeEmail,
  spaceName,
  spaceType,
  spaceFloor,
  from,
  to,
  confirmedAt,
  status,
}: Props) => {
  const { t } = useTranslation(['bookings', 'common', 'spaces']);

  const { data: organizationDetails } = useOrganizationDetails();

  const tenantTimezone = organizationDetails?.timezone;

  return (
    <TableRow>
      <TableCell>
        <Typography variant="body2">{employeeEmail}</Typography>
      </TableCell>

      <TableCell>
        <Box>
          <Typography variant="body2">{spaceName}</Typography>
          <Typography variant="body2" color="text.secondary">
            {`${t(spaceTypesTranslations[spaceType])}; ${t('spaces:common.columns.floor')} ${spaceFloor}`}
          </Typography>
        </Box>
      </TableCell>

      <TableCell>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}>
          <Box>
            <Typography color="inherit" variant="inherit">
              <FormatDate
                date={new TZDate(from, tenantTimezone)}
                format={`${dateFormats.day} ${dateFormats.monthShort}, ${dateFormats.yearShort}`}
              />
            </Typography>
            <Typography color="textSecondary" variant="inherit">
              <FormatDate
                date={new TZDate(from, tenantTimezone)}
                format={`${dateFormats.hours}:${dateFormats.minutes}`}
              />
            </Typography>
          </Box>
          {' - '}
          <Box>
            <Typography color="inherit" variant="inherit">
              <FormatDate
                date={new TZDate(to, tenantTimezone)}
                format={`${dateFormats.day} ${dateFormats.monthShort}, ${dateFormats.yearShort}`}
              />
            </Typography>
            <Typography color="textSecondary" variant="inherit">
              <FormatDate
                date={new TZDate(to, tenantTimezone)}
                format={`${dateFormats.hours}:${dateFormats.minutes}`}
              />
            </Typography>
          </Box>
        </Box>
      </TableCell>

      <TableCell>
        {confirmedAt ? (
          <Status
            color={bookingPresenceColors.PRESENT}
            label={t(bookingPresenceTranslations.PRESENT)}
          />
        ) : (
          <Status
            color={bookingPresenceColors.ABSENT}
            label={t(bookingPresenceTranslations.ABSENT)}
          />
        )}
      </TableCell>

      <TableCell>
        <Status
          color={bookingStateColors[getBookingStatus(from, to, status)]}
          label={t(
            bookingStateTranslations[getBookingStatus(from, to, status)],
          )}
        />
      </TableCell>
    </TableRow>
  );
};
